import React, { useMemo } from 'react';
import classNames from 'classnames';
import {
	FetchTransactionsCount,
	FetchTransactionsWithFilters,
} from 'Util/_HumanWritten/Transactions/FetchTransactionsWithFilters';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import StaffTransactionFilters from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/StaffTransactionFilters';
import { useStaffTransactionsStore } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/StaffTransactionsFilterStore';
import { StaffTransactionsCollection } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionsTableComponent';
import If from 'Views/Components/If/If';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounce } from 'Hooks/useDebounce';
import { STAFF_TABLE_SECTIONS, useResetFiltersOnSectionEntry } from 'Util/_HumanWritten/Navigation/staffTableSections';

export interface StaffTransactionsTableAndFiltersProps {
	userId?: string;
	resultsPerPage?: number;
	onResultsPage?: boolean;
}

// --- Main Component ---
export function StaffTransactionsListOuter({
	userId,
	resultsPerPage,
	onResultsPage,
}: StaffTransactionsTableAndFiltersProps) {
	const perPage = isNotNullOrUndefined(resultsPerPage) ? resultsPerPage : 10;

	// Above the store subscription on purpose — see useResetFiltersOnSectionEntry.
	useResetFiltersOnSectionEntry(STAFF_TABLE_SECTIONS.TRANSACTIONS, () =>
		useStaffTransactionsStore.getState().resetFilters(),
	);

	const { filters, updateFilters, selectedTransactionIds, setSelectedTransactionIds } = useStaffTransactionsStore();
	const debouncedSearchFilters = useDebounce(
		{
			...filters,
			pageNo: undefined,
		},
		150,
	);

	const queryFilters = useMemo(
		() => ({
			...debouncedSearchFilters,
			pageNo: filters.pageNo,
		}),
		[debouncedSearchFilters, filters.pageNo],
	);

	const MINIMUM_LOADER_TIME = 300;

	const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
		queryKey: ['staffTransactions', userId, queryFilters, perPage],
		queryFn: async () => {
			const apiPromise = FetchTransactionsWithFilters({
				searchString: queryFilters.searchString,
				startDate: queryFilters.startDate,
				endDate: queryFilters.endDate,
				filterByCreatedDate: queryFilters.filterByCreatedDate,
				statusFilters: queryFilters.statusFilters,
				locationFilters: queryFilters.locationFilters,
				createdByFilters: queryFilters.createdByFilters,
				typeFilters: queryFilters.typeFilters,
				actionFilters: queryFilters.actionFilters,
				orderBy: queryFilters.orderBy,
				orderByDescending: queryFilters.orderByDescending,
				skip: perPage * (queryFilters.pageNo || 0),
				take: perPage,
				userId: userId,
				initialFetch: true,
				skipCount: true,
			});

			const [result] = await Promise.all([
				apiPromise,
				new Promise(resolve => setTimeout(resolve, MINIMUM_LOADER_TIME)),
			]);

			return result;
		},
		placeholderData: keepPreviousData,
		staleTime: 1000 * 60, // Data is fresh for 1 minute (adjust as needed)
	});

	// The total, fetched on its own. The COUNT is most of the list request's cost (it walks every
	// transaction), so it no longer holds up the rows, and its key leaves out the page and the sort
	// order — neither changes the total — so paging reuses it instead of recounting. Same
	// 'staffTransactions' prefix, so anything invalidating the list invalidates the total too.
	const countFilters = useMemo(() => {
		const { orderBy, orderByDescending, pageNo, ...rest } = debouncedSearchFilters;
		return rest;
	}, [debouncedSearchFilters]);
	const { data: totalCount, refetch: refetchCount } = useQuery({
		queryKey: ['staffTransactions', 'count', userId, countFilters],
		queryFn: () =>
			FetchTransactionsCount({
				searchString: countFilters.searchString,
				startDate: countFilters.startDate,
				endDate: countFilters.endDate,
				filterByCreatedDate: countFilters.filterByCreatedDate,
				statusFilters: countFilters.statusFilters,
				locationFilters: countFilters.locationFilters,
				createdByFilters: countFilters.createdByFilters,
				typeFilters: countFilters.typeFilters,
				actionFilters: countFilters.actionFilters,
				userId: userId,
				initialFetch: true,
			}),
		staleTime: 1000 * 60,
	});

	const handleRefresh = () => {
		refetch();
		refetchCount();
	};

	return (
		<div className={classNames('frontend-admin-page', 'staff-transactions')}>
			{!onResultsPage && (
				<>
					<div className="frontend-admin-page__layout--wide">
						<h1>Transactions</h1>
					</div>
					<div className="page-header__border" />
				</>
			)}

			<StaffTransactionFilters setRefresh={handleRefresh} totalTransactionCount={totalCount ?? 0} />

			<StaffTransactionsCollection
				className="staff__table"
				transactions={data?.data ?? null}
				totalTransactionCount={totalCount}
				perPage={perPage}
				filters={filters}
				setFilters={updateFilters}
				isLoading={isLoading || isFetching}
				selectedTransactionIds={selectedTransactionIds}
				setSelectedTransactionIds={setSelectedTransactionIds}
				lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : new Date()}
				refresh={handleRefresh}
				hideCustomerColumn={onResultsPage}
			/>
		</div>
	);
}
